// =============================================================================
// AutoCRP – centrální definice oprávnění
// =============================================================================

import { usePermStore } from '../stores/permStore';

export type AppRole = 'ADMIN' | 'MANAGER' | 'SALESPERSON' | 'ACCOUNTANT' | 'VIEWER';

export type Permission =
  // Vozidla
  | 'vehicles.view' | 'vehicles.create' | 'vehicles.edit' | 'vehicles.delete' | 'vehicles.sell' | 'vehicles.uploadPhoto'
  | 'vehicles.viewMargin'        // vidí marži a investice
  | 'vehicles.viewPurchasePrice' // vidí nákupní cenu
  // Zákazníci
  | 'customers.view' | 'customers.create' | 'customers.edit' | 'customers.delete'
  // Faktury
  | 'invoices.view' | 'invoices.create' | 'invoices.edit' | 'invoices.delete'
  // Investice a dodavatelé
  | 'investments.view' | 'investments.create' | 'investments.edit' | 'investments.delete'
  // Statistiky
  | 'statistics.view'
  | 'statistics.viewFinancial'   // vidí finanční údaje (tržby, marže…)
  // Inzerce
  | 'advertising.view' | 'advertising.publish' | 'advertising.edit'
  // Sklad
  | 'inventory.view' | 'inventory.consume' | 'inventory.manage'
  // Servis
  | 'service.view' | 'service.edit' | 'service.admin'
  // Poprodejní služby
  | 'aftersales.view' | 'aftersales.edit' | 'aftersales.admin'
  // Pokladna a banka
  | 'cashbook.manage' | 'cashbook.payments' | 'cashbook.reopen'
  // Dokumenty a e-mail
  | 'esign.use' | 'esign.history' | 'mail.send'
  // Nastavení
  | 'settings.view'
  | 'settings.editCompany'       // firma, číselné řady, šablony, e-maily
  | 'settings.editSystem'        // pouze ADMIN
  | 'users.manage';              // správa uživatelů a oprávnění – pouze ADMIN

// ── Výchozí oprávnění rolí ───────────────────────────────────────────────────
// Slouží jen jako záloha, dokud se nenačtou skutečná oprávnění ze serveru (Nastavení → Uživatelé →
// Oprávnění rolí). Musí odpovídat DEFAULT_PERMISSIONS v apps/api/src/shared/utils/permissions.ts.
const ALL_PERMISSIONS: Permission[] = [
  'vehicles.view','vehicles.create','vehicles.edit','vehicles.delete','vehicles.sell','vehicles.uploadPhoto','vehicles.viewMargin','vehicles.viewPurchasePrice',
  'customers.view','customers.create','customers.edit','customers.delete',
  'invoices.view','invoices.create','invoices.edit','invoices.delete',
  'investments.view','investments.create','investments.edit','investments.delete',
  'statistics.view','statistics.viewFinancial','advertising.view','advertising.publish','advertising.edit',
  'inventory.view','inventory.consume','inventory.manage','service.view','service.edit','service.admin',
  'aftersales.view','aftersales.edit','aftersales.admin','cashbook.manage','cashbook.payments','cashbook.reopen',
  'esign.use','esign.history','mail.send','settings.view','settings.editCompany','settings.editSystem','users.manage',
];
const ROLE_PERMISSIONS: Record<AppRole, Permission[]> = {
  ADMIN: ALL_PERMISSIONS,
  MANAGER: ALL_PERMISSIONS.filter(p => p !== 'settings.editSystem' && p !== 'users.manage'),
  SALESPERSON: [
    'vehicles.view','vehicles.create','vehicles.edit','vehicles.sell','vehicles.uploadPhoto',
    'customers.view','customers.create','customers.edit',
    'invoices.view','invoices.create','invoices.edit',
    'investments.view','investments.create','investments.edit',
    'advertising.view','advertising.publish','inventory.view','inventory.consume','service.view','service.edit',
    'aftersales.view','aftersales.edit','cashbook.payments','esign.use','mail.send',
  ],
  ACCOUNTANT: [
    'vehicles.view','vehicles.viewMargin','vehicles.viewPurchasePrice','customers.view',
    'invoices.view','invoices.create','invoices.edit','invoices.delete',
    'investments.view','statistics.view','statistics.viewFinancial','advertising.view',
    'inventory.view','service.view','aftersales.view','cashbook.manage','cashbook.payments','mail.send',
  ],
  VIEWER: [
    'vehicles.view','customers.view','invoices.view','investments.view','statistics.view','advertising.view',
    'inventory.view','service.view','aftersales.view',
  ],
};

// ── Helper funkce ─────────────────────────────────────────────────────────────
/** Skutečná oprávnění přihlášené role (ze serveru); do načtení platí výchozí. */
export function hasPermission(role: AppRole | string | undefined, permission: Permission): boolean {
  if (!role) return false;
  // Oprávnění načtená ze serveru patří právě přihlášenému uživateli (při odhlášení se mažou)
  const st = usePermStore.getState();
  if (st.perms) return st.perms.includes(permission);
  if (role === 'ADMIN') return true;
  const perms = ROLE_PERMISSIONS[role as AppRole];
  return !!perms && perms.includes(permission);
}

export function hasAnyPermission(role: AppRole | string | undefined, permissions: Permission[]): boolean {
  return permissions.some(p => hasPermission(role, p));
}

export const ROLE_LABELS: Record<AppRole, string> = {
  ADMIN:       'Správce',
  MANAGER:     'Manažer',
  SALESPERSON: 'Prodejce',
  ACCOUNTANT:  'Účetní',
  VIEWER:      'Čtenář',
};

export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  ADMIN:       'Plný přístup ke všem funkcím včetně správy uživatelů a systému',
  MANAGER:     'Výchozí: plný přístup k bazaru včetně smazání, bez správy systému',
  SALESPERSON: 'Výchozí: vozidla, zákazníci, faktury, sklad a servis — bez mazání a financí',
  ACCOUNTANT:  'Výchozí: faktury, pokladna, statistiky a marže — bez editace vozidel',
  VIEWER:      'Výchozí: pouze čtení — nemůže nic měnit',
};
